import { useRef } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  CheckCircle2, XCircle, AlertCircle, Eye, Trash2, FileText, Sparkles, Upload,
  Loader2, Lightbulb, Search, Download,
} from 'lucide-react';
import { usePortalI18n } from '../context/PortalI18nContext';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────────
// PerSlugUploader
// One self-contained row per required document.
//
// Translation: uses the PortalI18n context when present (i.e. when this
// component is rendered inside `/portal/*`). Outside the portal it falls
// back to English transparently — the hook returns a stable default.
// ─────────────────────────────────────────────────────────────────────────
const PerSlugUploader = ({
  slug, label, description, atts, aiByFilename, samples = [], policyId,
  editable, uploading, onUpload, onDelete, onPreview, isOrphan = false, reviewing = false,
}) => {
  const { t, tDocType } = usePortalI18n();
  const inputRef = useRef(null);
  const hasFile = atts.length > 0;
  const friendly = label || tDocType(slug);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await onUpload(file, slug);
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div
      className={`rounded-xl border bg-card overflow-hidden ${hasFile ? '' : 'border-rose-200/70 bg-rose-50/30'}`}
      data-testid={`uploader-${slug}`}
    >
      {/* ── Header: title + status pill + (replace) button — single row */}
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-border/50">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-[15px] leading-tight">{friendly}</h4>
            {isOrphan ? (
              <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                {t('docs.extraBadge')}
              </Badge>
            ) : hasFile ? (
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] gap-1">
                <CheckCircle2 className="h-2.5 w-2.5" /> {t('docs.uploadedBadge')}
              </Badge>
            ) : (
              <Badge variant="outline" className="text-[10px] text-rose-700 border-rose-300 gap-1">
                <XCircle className="h-2.5 w-2.5" /> {t('docs.pendingBadge')}
              </Badge>
            )}
          </div>
          {description && (
            <div className="mt-2 rounded-md border border-emerald-200/70 bg-emerald-50/60 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-800/90 mb-1">
                {t('docs.checklistHeading')}
              </div>
              {/* Render the description as-is. The backend now generates a
                  ✓-prefixed bulleted checklist (Iter 36); but legacy policies
                  whose description hasn't been re-saved still show a plain
                  paragraph here — both render acceptably. `whitespace-pre-line`
                  keeps the newlines the LLM produced. */}
              <p className="text-xs text-emerald-900/90 leading-relaxed whitespace-pre-line">
                {description}
              </p>
              <p className="mt-2 pt-2 border-t border-emerald-300/40 text-[11px] italic text-emerald-800/70 leading-snug">
                {t('docs.mpcaDisclaimer')}
              </p>
            </div>
          )}
          {!isOrphan && samples && samples.length > 0 && policyId && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {samples.map((s) => (
                <a
                  key={s.id}
                  href={`${API_BASE}/portal/policies/${policyId}/sample-documents/${s.id}/download`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-300 px-2 py-0.5 text-[10px] text-emerald-900 hover:bg-emerald-100 transition"
                  data-testid={`portal-uploader-sample-${s.id}`}
                  title={s.filename}
                >
                  <Download className="h-2.5 w-2.5" />
                  <span className="font-mono max-w-[140px] truncate">{s.filename}</span>
                </a>
              ))}
            </div>
          )}
        </div>
        {editable && !isOrphan && (
          <div className="shrink-0">
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept="application/pdf"
              onChange={onFileChange}
              data-testid={`upload-input-${slug}`}
            />
            <Button
              type="button"
              size="sm"
              variant={hasFile ? 'outline' : 'default'}
              onClick={() => inputRef.current?.click()}
              disabled={uploading || reviewing}
              title={reviewing ? t('docs.uploadBlockedAi') : undefined}
              data-testid={`upload-button-${slug}`}
            >
              {uploading ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{t('common.uploading')}</>
              ) : reviewing ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin text-muted-foreground" />{t('docs.aiProcessing')}</>
              ) : (
                <><Upload className="h-3.5 w-3.5 mr-1.5" />{hasFile ? t('common.replace') : t('common.upload')}</>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* ── Body: uploaded file(s) + AI verdict compacted ─────────── */}
      {atts.length > 0 && (
        <div className="px-4 py-3 space-y-3 bg-muted/20">
          {atts.map((a) => {
            const v = (a.id && aiByFilename[a.id]) || aiByFilename[a.filename];
            const verdict = v?.verdict;
            const isAccepted = verdict === 'accepted'
              && (!v?.matched_slug || isOrphan || v.matched_slug === slug);
            const wrongSlug = verdict === 'accepted' && v?.matched_slug && !isOrphan && v.matched_slug !== slug;
            const verdictColor = !verdict
              ? 'border-l-muted-foreground/30'
              : isAccepted
                ? 'border-l-emerald-500'
                : wrongSlug
                  ? 'border-l-amber-500'
                  : 'border-l-rose-500';
            return (
              <div key={a.id}>
                {/* Compact file chip */}
                <div className="flex items-center gap-2 rounded-md bg-card border px-2.5 py-2"
                     data-testid={`file-chip-${a.id}`}>
                  <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="font-mono text-xs truncate flex-1 min-w-0">{a.filename}</span>
                  <span className="text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                    {a.size_bytes ? `${Math.round(a.size_bytes / 1024)} KB` : ''}
                  </span>
                  {!verdict ? (
                    <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground">
                      <Sparkles className={`h-2.5 w-2.5 ${reviewing ? 'animate-spin' : 'animate-pulse'}`} /> {reviewing ? 'Processing…' : t('docs.aiPending')}
                    </Badge>
                  ) : isAccepted ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px] gap-1">
                      <CheckCircle2 className="h-2.5 w-2.5" /> {t('docs.accepted')}
                    </Badge>
                  ) : wrongSlug ? (
                    <Badge className="bg-amber-600 hover:bg-amber-600 text-[10px] gap-1">
                      <AlertCircle className="h-2.5 w-2.5" /> {t('docs.wrongSlot')}
                    </Badge>
                  ) : (
                    <Badge className="bg-rose-600 hover:bg-rose-600 text-[10px] gap-1">
                      <XCircle className="h-2.5 w-2.5" /> {t('docs.rejected')}
                    </Badge>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onPreview(a)} title={t('common.preview')}>
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  {editable && onDelete && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDelete(a.id)} title={t('common.remove')}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                    </Button>
                  )}
                </div>

                {/* Compact AI feedback — single panel with a verdict-tinted
                    left bar and two stacked subitems. Replaces the two
                    big blue/yellow cards that made the page feel busy. */}
                {v && (v.observation || v.recommendation) && (
                  <div className={`mt-2 ml-0 border-l-2 ${verdictColor} bg-card rounded-r-md`}>
                    <div className="px-3 py-2 space-y-1.5">
                      {v.observation && (
                        <div className="flex items-start gap-1.5 text-[11px] leading-snug">
                          <Search className="h-3 w-3 mt-0.5 text-sky-600 shrink-0" />
                          <div>
                            <span className="font-semibold text-foreground/80">{t('docs.aiObservation')}: </span>
                            <span className="text-foreground/70">{v.observation}</span>
                          </div>
                        </div>
                      )}
                      {v.recommendation && (
                        <div className="flex items-start gap-1.5 text-[11px] leading-snug">
                          <Lightbulb className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" />
                          <div>
                            <span className="font-semibold text-foreground/80">{t('docs.aiRecommendation')}: </span>
                            <span className="text-foreground/70">{v.recommendation}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Public API mirrors RequiredDocsChecklist + adds `onUpload` + `uploadingSlug`.
// `descriptions_hi` (optional) — admin's Hindi-language versions of the
// per-doc descriptions. When the user is viewing the portal in Hindi we
// prefer the Hindi blurb, falling back to English when absent.
export const RequiredDocsUploader = ({
  claim,
  editable = false,
  uploadingSlug = null,
  aiRunning = false,
  onUpload,
  onDelete,
  onPreview,
}) => {
  const { lang, tDocType } = usePortalI18n();
  const required = claim?.policy?.required_documents || [];
  const descriptionsEn = claim?.policy?.required_documents_descriptions || {};
  const descriptionsHi = claim?.policy?.required_documents_descriptions_hi || {};
  // Iter 36: secretary-facing checklists (auto-generated by backend from
  // the strict admin AI-instruction). Show this whenever present —
  // falls back to the admin description (the old behaviour) so secretaries
  // on policies that haven't been re-saved since the upgrade still see
  // SOMETHING. The strict admin description was scaring secretaries with
  // auditor language ("must contain", "REJECT if absent"). The checklist
  // is the same content rewritten in supportive bullet form.
  const checklistsEn = claim?.policy?.required_documents_user_checklists || {};
  const checklistsHi = claim?.policy?.required_documents_user_checklists_hi || {};
  const samplesBySlug = claim?.policy?.sample_documents || {};
  const policyId = claim?.policy?.id || claim?.policy_id;
  const pickDesc = (slug) => {
    // Prefer the user-facing checklist when available.
    if (lang === 'hi') {
      return (
        checklistsHi[slug]
        || checklistsEn[slug]
        || descriptionsHi[slug]
        || descriptionsEn[slug]
        || ''
      );
    }
    return checklistsEn[slug] || descriptionsEn[slug] || '';
  };
  const attachments = claim?.attachments || [];
  const perDoc = claim?.ai_review?.per_doc || [];
  // AI review is intentionally NOT used to disable uploads anymore.
  // Secretaries must be free to upload further documents at any time,
  // even while a previous batch is still being reviewed. We keep the
  // flag for read-only display (e.g. status pills) but never block.
  const reviewing = false;

  // Single lookup keyed by BOTH the stable attachment_id (preferred) and the
  // filename (legacy fallback). Matching by filename alone left files stuck on
  // "AI pending" whenever the AI echoed a different name or omitted an entry.
  const aiByFilename = {};
  for (const v of perDoc) {
    if (v?.filename && !(v.filename in aiByFilename)) aiByFilename[v.filename] = v;
    if (v?.attachment_id) aiByFilename[v.attachment_id] = v;
  }
  const attsBySlug = {};
  for (const a of attachments) {
    const slug = a.doc_type || 'other';
    (attsBySlug[slug] = attsBySlug[slug] || []).push(a);
  }
  const orphanAtts = required.length > 0
    ? attachments.filter((a) => !required.includes(a.doc_type))
    : [];

  if (required.length === 0) {
    return (
      <div className="space-y-2">
        {attachments.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            {/* No docs */}
          </div>
        ) : (
          attachments.map((a) => (
            <PerSlugUploader
              key={a.id}
              slug={a.doc_type || 'other'}
              label={tDocType(a.doc_type || 'other')}
              description=""
              atts={[a]}
              aiByFilename={aiByFilename}
              reviewing={reviewing}
              editable={editable}
              uploading={false}
              onUpload={onUpload}
              onDelete={onDelete}
              onPreview={onPreview}
            />
          ))
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {required.map((slug) => (
        <PerSlugUploader
          key={slug}
          slug={slug}
          label={tDocType(slug)}
          description={pickDesc(slug)}
          atts={attsBySlug[slug] || []}
          aiByFilename={aiByFilename}
          reviewing={reviewing}
          samples={samplesBySlug[slug] || []}
          policyId={policyId}
          editable={editable}
          uploading={uploadingSlug === slug}
          onUpload={onUpload}
          onDelete={onDelete}
          onPreview={onPreview}
        />
      ))}
      {orphanAtts.length > 0 && (
        <div className="space-y-2 pt-2 border-t border-dashed">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {/* Extra uploads — translated via tDocType inside the chip */}
          </div>
          {orphanAtts.map((a) => (
            <PerSlugUploader
              key={a.id}
              slug={a.doc_type || 'other'}
              label={tDocType(a.doc_type || 'other')}
              description=""
              atts={[a]}
              aiByFilename={aiByFilename}
              reviewing={reviewing}
              editable={editable}
              uploading={false}
              onUpload={onUpload}
              onDelete={onDelete}
              onPreview={onPreview}
              isOrphan
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RequiredDocsUploader;
